import type { PCDispatchReport } from '../contexts/QueueDispatchContext';

// THE one answer to "which PC does the next visitor land on".
//
// It used to be three answers (Dirk 2026-09-16): the bar promised the
// alphabetically first free PC, the wachtrij ordered by minutes-left, and the
// pop-up preselected by router load and rest time. So the bar could say "Vrij →
// Bakker 2" and the pop-up would open on PC 6, both correctly by their own
// rule. Whatever the pop-up would actually launch on is the one that matters,
// so that rule lives here and everything else reads it.
//
// Two of the three also sorted names without `numeric`, which puts
// ANDROMEDA-10 before ANDROMEDA-2. A ten-PC room hits that every day.

/** Natural sort so PC-2 < PC-10. The ONLY name comparison in the dispatch path. */
export function byPcName(a: { pcName: string }, b: { pcName: string }): number {
  return a.pcName.localeCompare(b.pcName, undefined, { numeric: true });
}

export interface DispatchCandidate {
  pcId: string;
  pcName: string;
  canLaunch: boolean;
  /** This PC has the build the visitor is booked for. */
  hasExperience: boolean;
}

/**
 * Free PCs, in name order. "Free" excludes `recovering`: the experience died
 * but the previous visitor is still on that PC (relaunch in flight, or the
 * agent gave up and nobody has decided what happens to them yet). It reads as
 * free and must never be offered.
 */
export function freeCandidates(
  reports: ReadonlyMap<string, PCDispatchReport>,
  hasExperience: (pcId: string) => boolean,
): DispatchCandidate[] {
  const rows: DispatchCandidate[] = [];
  for (const [pcId, r] of reports) {
    if (!r.online || r.running || r.recovering) continue;
    rows.push({ pcId, pcName: r.pcName, canLaunch: r.canLaunch, hasExperience: hasExperience(pcId) });
  }
  return rows.sort(byPcName);
}

/** PCs each network is already carrying. Running OR recovering counts as load. */
export function networkLoad(
  reports: ReadonlyMap<string, PCDispatchReport>,
  networkOf: (pcId: string) => string | null,
): Map<string, number> {
  const load = new Map<string, number>();
  for (const [pcId, r] of reports) {
    if (!r.online || !(r.running || r.recovering)) continue;
    const net = networkOf(pcId) ?? 'unknown';
    load.set(net, (load.get(net) ?? 0) + 1);
  }
  return load;
}

export interface PickArgs {
  candidates: DispatchCandidate[];
  /** How many visitors that PC's router is already carrying. */
  networkLoadOf: (pcId: string) => number;
  /** Epoch ms of that PC's last run; 0 = never used today, which sorts oldest. */
  lastUsedAt: (pcId: string) => number;
  /** A move must not offer the PC we just stopped, unless it is the only one. */
  excludePcId?: string | null;
}

/**
 * Auto-pick, in order: the least loaded router, then the PC that has been idle
 * longest, then name order so the choice is stable across refetches.
 *
 * Spreading stays first because headsets stream over WiFi and five visitors on
 * one router costs picture quality (Dirk 2026-09-10). Longest-idle replaced
 * "prefer the PC that just came free" (Dirk 2026-09-11): the just-freed PC is
 * the one whose headset is dirtiest and least charged, so preferring it meant
 * staff were always cleaning under time pressure while rested stations sat
 * unused.
 */
export function pickNextPc({
  candidates,
  networkLoadOf,
  lastUsedAt,
  excludePcId,
}: PickArgs): string | null {
  let eligible = candidates.filter((p) => p.canLaunch && p.hasExperience);
  if (eligible.length === 0) return null;
  if (excludePcId && eligible.some((p) => p.pcId !== excludePcId)) {
    eligible = eligible.filter((p) => p.pcId !== excludePcId);
  }
  const lightest = Math.min(...eligible.map((p) => networkLoadOf(p.pcId)));
  const spread = eligible.filter((p) => networkLoadOf(p.pcId) === lightest);
  const best = [...spread].sort((x, y) => {
    const dx = lastUsedAt(x.pcId);
    const dy = lastUsedAt(y.pcId);
    return dx !== dy ? dx - dy : byPcName(x, y);
  });
  return best[0].pcId;
}
